// src/pages/admin/BrokerApplicationsPanel.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  getFirestore,
  collection,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import app from "../../firebase";
import { FiCheck, FiX, FiClock } from "react-icons/fi";

const db = getFirestore(app);

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const statusBadge = (status) => {
  const map = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${map[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
};

// Embeds into AdminPanel.js as a tab, like PartnerApplicationsPanel.
// Reads/writes the `broker_applications` collection — kept separate from
// the `brokers` collection so self-signups don't mix with manually-added,
// public-facing broker listings.
export default function BrokerApplicationsPanel() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [actioningId, setActioningId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "broker_applications"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setApplications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    if (activeTab === "all") return applications;
    return applications.filter((a) => a.status === activeTab);
  }, [applications, activeTab]);

  const ROLE_BY_STATUS = {
    approved: "Broker",
    rejected: "Broker Applicant",
    pending: "Broker Applicant",
  };

  const setStatus = async (id, status) => {
    setActioningId(id);
    try {
      // The application doc id is the user's uid, so this also updates
      // users/{uid}.role in lockstep — role can never drift from status.
      await Promise.all([
        updateDoc(doc(db, "broker_applications", id), {
          status,
          [`${status}At`]: serverTimestamp(),
        }),
        updateDoc(doc(db, "users", id), {
          role: ROLE_BY_STATUS[status] || "Broker Applicant",
        }),
      ]);
    } catch (err) {
      console.error(err);
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-200 flex-wrap">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              activeTab === tab.key
                ? "border-[#e31c3d] text-[#002b5c]"
                : "border-transparent text-gray-500 hover:text-[#002b5c]"
            }`}
          >
            {tab.label}
            {tab.key !== "all" && (
              <span className="ml-1 text-xs text-gray-400">
                ({applications.filter((a) => a.status === tab.key).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-gray-600 text-sm">Loading broker applications…</div>
      ) : filtered.length === 0 ? (
        <div className="text-gray-600 text-sm flex items-center gap-2">
          <FiClock /> No broker applications in this category.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((application) => (
            <div
              key={application.id}
              className="border border-gray-200 rounded-xl px-5 py-4 bg-white flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            >
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-[#002b5c]">{application.name || "Unnamed"}</p>
                  {statusBadge(application.status)}
                </div>
                <p className="text-sm text-gray-600">{application.email}</p>
                <p className="text-sm text-gray-600">{application.phone}</p>
              </div>

              {application.status === "pending" && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setStatus(application.id, "approved")}
                    disabled={actioningId === application.id}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition disabled:opacity-60"
                  >
                    <FiCheck /> Approve
                  </button>
                  <button
                    onClick={() => setStatus(application.id, "rejected")}
                    disabled={actioningId === application.id}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#e31c3d] hover:opacity-95 text-white text-sm font-semibold transition disabled:opacity-60"
                  >
                    <FiX /> Reject
                  </button>
                </div>
              )}

              {application.status !== "pending" && (
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setStatus(application.id, application.status === "approved" ? "rejected" : "approved")
                    }
                    disabled={actioningId === application.id}
                    className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-semibold transition disabled:opacity-60"
                  >
                    Move to {application.status === "approved" ? "Rejected" : "Approved"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
